import React, { useState, useEffect } from 'react';
import { ArrowLeft, RotateCcw, ChevronLeft, ChevronRight, Volume2, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { vocabularyAPI, categoriesAPI, progressAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { usePronunciation } from '../../hooks/usePronunciation';
import { playAudioFile, testAudioUrl, testBrowserAudioSupport, getRecommendedAudioFormat } from '../../utils/audioPlayer';

const Flashcards = () => {
  const BATCH_SIZE = 15; // Number of words per batch
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [allWords, setAllWords] = useState([]); // All vocabulary words
  const [currentBatch, setCurrentBatch] = useState([]); // Current batch of words
  const [batchNumber, setBatchNumber] = useState(0);
  const [categories, setCategories] = useState([]);
  const [completedWords, setCompletedWords] = useState(new Set());
  const [struggledWords, setStruggledWords] = useState([]); // Words marked as "Need More Practice"
  const [wordPerformance, setWordPerformance] = useState({}); // Track how many times each word was reviewed
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { speak, isSupported: speechSupported, isLoading: speechLoading } = usePronunciation();

  useEffect(() => {
    loadVocabulary();
    loadCategories();
    
    // Test browser audio support on component mount
    const audioSupport = testBrowserAudioSupport();
    const recommendedFormat = getRecommendedAudioFormat();
    console.log('Recommended audio format for this browser:', recommendedFormat);
  }, []);

  const loadVocabulary = async () => {
    try {
      setLoading(true);
      const response = await vocabularyAPI.getAll();
      const shuffledWords = (response.words || []).sort(() => 0.5 - Math.random());
      setAllWords(shuffledWords);
      
      // Load first batch
      loadBatch(shuffledWords, 0);
    } catch (error) {
      toast({
        title: "Error Loading Vocabulary",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const loadBatch = (words, batchNum) => {
    const startIdx = batchNum * BATCH_SIZE;
    const endIdx = startIdx + BATCH_SIZE;
    const batch = words.slice(startIdx, endIdx);
    
    setCurrentBatch(batch);
    setBatchNumber(batchNum);
    setCurrentIndex(0);
    setIsFlipped(false);
    
    toast({
      title: `📚 Batch ${batchNum + 1} of ${Math.ceil(words.length / BATCH_SIZE)}`,
      description: `Learning ${batch.length} words`,
    });
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await categoriesAPI.getAll();
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const currentWord = currentBatch[currentIndex];
  const progress = ((currentIndex + 1) / currentBatch.length) * 100;
  const totalBatches = Math.ceil(allWords.length / BATCH_SIZE);
  const hasNextBatch = batchNumber < totalBatches - 1;

  const nextCard = () => {
    if (currentIndex < currentBatch.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    } else {
      // End of current batch
      if (struggledWords.length > 0) {
        // Review struggled words from this batch
        reviewStruggledWords();
      } else if (hasNextBatch) {
        // Move to next batch
        showBatchComplete();
      } else {
        // All batches complete
        showAllComplete();
      }
    }
  };
  
  const showBatchComplete = () => {
    toast({
      title: "🎉 Batch Complete!",
      description: `Great work! Ready for the next ${BATCH_SIZE} words?`,
      duration: 5000,
    });
  };
  
  const showAllComplete = () => {
    toast({
      title: "🎊 All Flashcards Complete!",
      description: `You've completed all ${allWords.length} words!`,
      duration: 5000,
    });
  };
  
  const loadNextBatch = () => {
    loadBatch(allWords, batchNumber + 1);
    setStruggledWords([]);
    setCompletedWords(new Set());
  };
  
  const reviewStruggledWords = () => {
    // Create a new batch with struggled words (with increased frequency)
    const reviewBatch = [];
    struggledWords.forEach(word => {
      const timesStruggled = wordPerformance[word.id]?.struggled || 1;
      // Add word multiple times based on struggle count (max 3 times)
      const repetitions = Math.min(timesStruggled, 3);
      for (let i = 0; i < repetitions; i++) {
        reviewBatch.push(word);
      }
    });
    
    // Shuffle review batch
    const shuffled = reviewBatch.sort(() => 0.5 - Math.random());
    
    setCurrentBatch(shuffled);
    setCurrentIndex(0);
    setIsFlipped(false);
    setStruggledWords([]); // Clear after creating review batch
    
    toast({
      title: "📖 Review Time!",
      description: `Let's practice the ${new Set(shuffled.map(w => w.id)).size} words you're working on`,
      duration: 3000,
    });
  };

  const prevCard = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  const flipCard = () => {
    setIsFlipped(!isFlipped);
  };

  const markAsKnown = async () => {
    setCompletedWords(prev => new Set([...prev, currentWord.id]));
    
    // Track performance
    setWordPerformance(prev => ({
      ...prev,
      [currentWord.id]: {
        ...prev[currentWord.id],
        known: (prev[currentWord.id]?.known || 0) + 1,
        lastReviewed: new Date().toISOString()
      }
    }));
    
    // Remove from struggled words if it was there
    setStruggledWords(prev => prev.filter(w => w.id !== currentWord.id));
    
    toast({
      title: "✅ Great job!",
      description: `You know "${currentWord.algerianWord}"!`,
    });
    
    // Update progress
    try {
      await progressAPI.update({
        category: currentWord.category,
        wordId: currentWord.id,
        gameType: "flashcards",
        score: 1,
        completed: true
      });
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
    
    nextCard();
  };
  
  const markNeedsPractice = () => {
    // Track that user struggled with this word
    setWordPerformance(prev => ({
      ...prev,
      [currentWord.id]: {
        ...prev[currentWord.id],
        struggled: (prev[currentWord.id]?.struggled || 0) + 1,
        lastReviewed: new Date().toISOString()
      }
    }));
    
    // Add to struggled words if not already there
    if (!struggledWords.find(w => w.id === currentWord.id)) {
      setStruggledWords(prev => [...prev, currentWord]);
    }
    
    toast({
      title: "📚 Added to review",
      description: `We'll practice "${currentWord.algerianWord}" again`,
    });
    
    nextCard();
  };

  const playPronunciation = async () => {
    const displayText = currentWord.algerianWord;
    
    // Check if we have recorded audio
    if (currentWord.audioUrl) {
      // Handle both Cloudinary URLs (https://) and local URLs (/api/audio/)
      let audioUrl;
      if (currentWord.audioUrl.startsWith('http')) {
        // Cloudinary URL - use directly
        audioUrl = currentWord.audioUrl;
      } else {
        // Local URL - prepend backend URL with cache-busting
        const cacheBuster = `?t=${Date.now()}`;
        audioUrl = `${process.env.REACT_APP_BACKEND_URL}${currentWord.audioUrl}${cacheBuster}`;
      }
      try {
        console.log('Attempting to play audio:', audioUrl);
        
        // Test if audio URL is accessible
        const urlTest = await testAudioUrl(audioUrl);
        console.log('Audio URL test:', urlTest);
        
        if (!urlTest.accessible) {
          throw new Error(`Audio file not accessible (${urlTest.status}): ${urlTest.error || 'Unknown error'}`);
        }
        
        if (!urlTest.isAudioFile) {
          console.warn('File may not be a valid audio file:', urlTest.contentType);
        }
        
        toast({
          title: "🔊 Playing Authentic Pronunciation",
          description: `${displayText} (recorded by native speaker)${currentWord.arabicScript ? ` • ${currentWord.arabicScript}` : ''}`,
        });

        // Play recorded audio using enhanced audio player
        await playAudioFile(audioUrl);
        return;
      } catch (error) {
        console.error('Audio playback failed:', error);
        
        // Provide more specific error messages and diagnostics
        let errorDescription = error.message;
        const recommendedFormat = getRecommendedAudioFormat();
        
        if (error.message.includes('not supported')) {
          errorDescription = `Audio format not supported by your browser. Recommended format: ${recommendedFormat.toUpperCase()}. Try using Chrome, Firefox, or Safari.`;
        } else if (error.message.includes('Network error')) {
          errorDescription = 'Network error loading audio. Check your internet connection.';
        } else if (error.message.includes('timeout')) {
          errorDescription = 'Audio loading timed out. The file may be too large or server is slow.';
        } else if (error.message.includes('decoding')) {
          errorDescription = `Audio file appears corrupted or in unsupported format. Try re-uploading in ${recommendedFormat.toUpperCase()} format.`;
        }
        
        // Log detailed diagnostics
        console.error('Audio diagnostics:', {
          audioUrl,
          error: error.message,
          browserSupport: testBrowserAudioSupport(),
          recommendedFormat,
          currentWord: currentWord.algerianWord
        });
        
        toast({
          title: "Audio Playback Error",
          description: `${errorDescription} Falling back to text-to-speech.`,
          variant: "destructive"
        });
        
        // Show additional help for format issues
        if (error.message.includes('not supported') || error.message.includes('decoding')) {
          setTimeout(() => {
            toast({
              title: "💡 Audio Format Tip",
              description: `For better compatibility, try re-uploading audio in MP3 or WAV format.`,
            });
          }, 2000);
        }
        // Fall back to text-to-speech
      }
    }

    // Fallback to text-to-speech
    const textToSpeak = currentWord.arabicScript || currentWord.algerianWord;
    
    if (!speechSupported) {
      toast({
        title: "Pronunciation Guide",
        description: `${displayText}: ${currentWord.pronunciation || 'No pronunciation guide'}${currentWord.arabicScript ? ` • ${currentWord.arabicScript}` : ''}`,
      });
      return;
    }

    if (speechLoading) {
      toast({
        title: "Loading voices...",
        description: "Please wait for speech system to initialize",
      });
      return;
    }

    try {
      toast({
        title: "🔊 Playing Pronunciation",
        description: `${displayText} (${currentWord.pronunciation})${currentWord.arabicScript ? ` • ${currentWord.arabicScript}` : ''}`,
      });

      await speak(textToSpeak, { rate: 0.8, pitch: 1, volume: 1 });
    } catch (error) {
      console.error('Pronunciation failed:', error);
      toast({
        title: "Pronunciation Guide",
        description: `${displayText}: ${currentWord.pronunciation || 'Audio not available'}${currentWord.arabicScript ? ` • ${currentWord.arabicScript}` : ''}`,
        variant: "destructive"
      });
    }
  };

  const resetProgress = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
    setCompletedWords(new Set());
    setStruggledWords([]);
    setWordPerformance({});
    if (allWords.length > 0) {
      loadBatch(allWords, 0);
    }
  };

  const getCategoryInfo = () => {
    return categories.find(cat => cat.id === currentWord.category);
  };

  if (!currentWord) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">No flashcards available</h1>
        <Link to="/vocabulary">
          <Button>Add some vocabulary words first</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Link to="/games">
          <Button variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Games
          </Button>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Flashcards</h1>
        <Button variant="outline" size="sm" onClick={resetProgress}>
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset
        </Button>
      </div>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">
            Batch {batchNumber + 1} of {totalBatches} • Card {currentIndex + 1} of {currentBatch.length}
          </span>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-green-600">
              ✓ {completedWords.size} known
            </span>
            {struggledWords.length > 0 && (
              <span className="text-orange-600">
                📚 {struggledWords.length} to review
              </span>
            )}
          </div>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Flashcard */}
      <div className="mb-8 flex justify-center">
        <div 
          className="relative w-full max-w-md h-80 cursor-pointer perspective-1000"
          onClick={flipCard}
        >
          <Card className={`absolute inset-0 transition-transform duration-500 transform-style-preserve-3d ${
            isFlipped ? 'rotate-y-180' : ''
          } shadow-xl hover:shadow-2xl`}>
            {/* Front Side */}
            <CardContent className={`absolute inset-0 backface-hidden p-8 flex flex-col justify-center items-center text-center ${
              isFlipped ? 'opacity-0' : 'opacity-100'
            }`}>
              <div className="mb-4">
                <Badge 
                  variant="secondary" 
                  className="mb-2"
                  style={{ 
                    backgroundColor: `${getCategoryInfo()?.color}20`, 
                    color: getCategoryInfo()?.color 
                  }}
                >
                  {getCategoryInfo()?.icon} {getCategoryInfo()?.name}
                </Badge>
              </div>
              
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {currentWord.algerianWord}
              </h2>
              
              {currentWord.arabicScript && (
                <h3 className="text-2xl font-bold text-blue-800 mb-4" dir="rtl">
                  {currentWord.arabicScript}
                </h3>
              )}
              
              {currentWord.pronunciation && (
                <p className="text-gray-500 text-lg italic mb-4">
                  {currentWord.pronunciation}
                </p>
              )}
              
              <p className="text-gray-600 text-sm">
                Click to reveal translation
              </p>
            </CardContent>

            {/* Back Side */}
            <CardContent className={`absolute inset-0 backface-hidden rotate-y-180 p-8 flex flex-col justify-center items-center text-center ${
              isFlipped ? 'opacity-100' : 'opacity-0'
            }`}>
              <h2 className="text-3xl font-bold text-green-700 mb-4">
                {currentWord.englishTranslation}
              </h2>
              
              {currentWord.exampleSentence && (
                <p className="text-gray-600 text-center italic mb-4 leading-relaxed">
                  &ldquo;{currentWord.exampleSentence}&rdquo;
                </p>
              )}
              
              <Badge variant={currentWord.difficulty === 'beginner' ? 'default' : 'secondary'}>
                {currentWord.difficulty}
              </Badge>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Controls */}
      <div className="flex justify-center space-x-4 mb-6">
        <Button 
          variant="outline" 
          onClick={prevCard}
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>
        
        {currentWord.pronunciation && (
          <Button 
            variant="outline" 
            onClick={playPronunciation}
            disabled={speechLoading}
          >
            <Volume2 className="w-4 h-4 mr-2" />
            {speechLoading ? 'Loading...' : speechSupported ? 'Pronunciation' : 'Guide'}
          </Button>
        )}
        
        <Button 
          variant="outline" 
          onClick={nextCard}
          disabled={currentIndex === currentBatch.length - 1}
        >
          Next
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      {/* Action Buttons */}
      {isFlipped && (
        <div className="flex justify-center space-x-4">
          <Button 
            variant="outline"
            className="border-orange-300 hover:bg-orange-50 text-orange-700"
            onClick={markNeedsPractice}
          >
            📚 Need More Practice
          </Button>
          <Button 
            className="bg-gradient-to-r from-green-600 to-green-700 text-white"
            onClick={markAsKnown}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            I Know This!
          </Button>
        </div>
      )}
      
      {/* Batch Navigation */}
      {currentIndex === currentBatch.length - 1 && isFlipped && struggledWords.length === 0 && hasNextBatch && (
        <div className="mt-6 text-center">
          <Button 
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white"
            onClick={loadNextBatch}
          >
            Continue to Next Batch →
          </Button>
        </div>
      )}

      {/* Debug Audio Button (only show if there's an audio file) */}
      {currentWord.audioUrl && (
        <div className="mt-4 text-center">
          <Button 
            variant="outline" 
            size="sm"
            onClick={async () => {
              const audioUrl = `${process.env.REACT_APP_BACKEND_URL}${currentWord.audioUrl}`;
              const support = testBrowserAudioSupport();
              const urlTest = await testAudioUrl(audioUrl);
              
              toast({
                title: "Audio Debug Info",
                description: `URL: ${audioUrl.substring(0, 50)}... | Status: ${urlTest.status} | Type: ${urlTest.contentType} | MP3: ${support.mp3} | M4A: ${support.m4a}`,
              });
              
              console.log('Full audio debug:', {
                audioUrl,
                urlTest,
                browserSupport: support,
                currentWord: currentWord.algerianWord
              });
            }}
          >
            🔧 Debug Audio
          </Button>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-8 text-center text-gray-500 text-sm space-y-1">
        <p>💡 Click the card to flip it and see the translation</p>
        <p>✅ Mark words you know to move forward</p>
        <p>📚 Words you need practice with will appear more often</p>
        <p>🎯 Complete one batch at a time ({BATCH_SIZE} words per batch)</p>
      </div>
    </div>
  );
};

export default Flashcards;